<template>
    <div class="manage-clients client-user mb-5">
        <div v-if="(client == null || client.id < 0) && user.user_type === 'agency_admin'"> 
            <h2 class="mb-2">Agency Clients</h2>
            <p class="mb-3">The clients listed below are all of those you've created within the system or that are attached to your agency. <strong>You will be charged $2,500 for each new client that you create.</strong> This gives you three months of use; once this is up the client moves to "inactive" where charts & integrations stop functioning unless the agency is willing to pay for additional months.</p>
        </div>
        <div v-else>
            <h2 class="mb-2">{{ client && client.id > 0 ? "Update" : "Add" }} Client</h2>
        </div>
        <div class="card">
            <div class="card-body">
                <h3>Organization Profile</h3>
                <div class="d-md-flex d-block">
                    <div class="profile-image flex-shrink-1 px-2 pe-4">
                        <div class="profile-image-container mb-3">
                            <img 
                                :src="local_client.image || '/img/images/profile_image_placeholder.png'" 
                                :alt="local_client.name || 'Organization'" 
                                :key="local_client.image || 'placeholder'" 
                                class="profile-image"
                            >
                            <button 
                                type="button" 
                                class="btn btn-secondary btn-icon-only-sm" 
                                @click="openImageModal" 
                                data-bs-toggle="tooltip"
                                title="Change Logo"
                            >
                                <img src="/img/icons/avid-icons/upload.svg" alt="Change Logo" class="icon">
                            </button>
                        </div>
                        <div class="text-danger invalid-feedback" v-if="errors.image !== ''">{{ errors.image }}</div>
                    </div>

                    <div class="mt-md-0 mt-3 px-2 w-100">
                        <!-- Name -->
                        <div class="mb-3">
                            <label class="form-label">Organization Name</label>
                            <v-select 
                                ref="orgSelect" 
                                :options="local_org_list" 
                                class="searchable-select column-display category"
                                v-model="local_client.name" 
                                v-if="client == null || client.id === -1"
                                :searchable="true" 
                                name="org_name" 
                                id="org_search"
                                @search="debouncedSearchOptions" 
                                :filterable="false"
                                @update:modelValue="selectOrg" 
                                :class="{ 'is-invalid': errors.name }"
                            >
                                <template #open-indicator="{ attributes }">
                                    <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                                </template>
                                <template #list-header="{ search }">
                                    <li class="border-bottom border-primary border-1" v-if="search !== ''">
                                        <a href="#" class="btn btn-link" @click.prevent="addNewOrg(search)">
                                            <i class="fas fa-circle-plus"></i> Add Organization
                                        </a>
                                    </li>
                                </template>
                                <template #option="{ value, label }">
                                    <li class="suboption show">{{ label }}</li>
                                </template>
                                <template #no-options="{ search }">
                                    <div class="suboption show mt-1" v-if="search === ''">
                                        <em>Type to search for the nonprofit...</em>
                                    </div>
                                    <div class="suboption show mt-1" v-else-if="searching">
                                        <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        Loading options
                                    </div>
                                    <div class="suboption show mt-1" v-else>
                                        <em>No organizations match your search</em>
                                    </div>
                                </template>
                            </v-select>
                            <input 
                                type="text" 
                                class="form-control" 
                                :class="{ 'is-invalid': errors.name }"
                                name="name" 
                                v-model="local_client.name" 
                                v-else
                             aria-label="Text input">
                            <div class="text-danger invalid-feedback" v-show="errors.name" v-html="errors.name"></div>
                        </div>

                        <!-- Admin-only fields -->
                        <div class="row" v-if="user.user_type === 'admin'">
                            <div class="col-md-6 col-12 mb-3">
                                <label class="form-label">Onboarding Status</label>
                                <select class="form-select" v-model="local_client.is_onboarding" aria-label="Onboarding Status">
                                    <option :value="true">In Onboarding</option>
                                    <option :value="false">Finished</option>
                                </select>
                            </div>
                            <div class="mb-3 col-md-6 col-12">
                                <label class="form-label">Referral Agency</label>
                                <select class="form-select" v-model="local_client.referring_agency_id" aria-label="Referral Agency">
                                    <option value="">Select Referral Agency</option>
                                    <option 
                                        v-for="agency in agencies" 
                                        :key="agency.id" 
                                        :value="agency.id"
                                    >
                                        {{ agency.name }}
                                    </option>
                                </select>
                            </div>
                        </div>   

                        <div class="row mb-3">
                            <div class="col-md-4 col-12">
                                <label class="form-label">Vertical</label>
                                <div class="dropdown">
                                    <select 
                                        class="form-select" 
                                        id="vertical" 
                                        v-model="local_client.vertical"
                                        :class="{ 'is-invalid': errors.vertical }" 
                                        @change="errors.vertical = ''"
                                     aria-label="Vertical">
                                        <option 
                                            v-for="item in vertical_types" 
                                            :key="item" 
                                            :value="item"
                                        >
                                            {{ item }}
                                        </option>
                                    </select>
                                </div>
                                <div class="text-danger invalid-feedback" v-show="errors.vertical">
                                    {{ errors.vertical }}
                                </div>
                            </div>
                            <div class="col-md-4 col-12">
                                <label class="form-label">Website Domain</label>
                                <input 
                                    type="text" 
                                    class="form-control" 
                                    :class="{ 'is-invalid': errors.domain }" 
                                    name="domain" 
                                    v-model="local_client.domain" 
                                    @change="errors.domain = ''"
                                 aria-label="Website Domain">
                                <div class="text-danger invalid-feedback" v-show="errors.domain">
                                    {{ errors.domain }}
                                </div>
                            </div>
                            <div class="col-md-4 col-12">
                                <label class="form-label">Data Residency
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="This is the region the client's data will reside in." alt=""></img>
                                </label>
                                <select class="form-select" v-model="local_client.region" :disabled="!canChangeRegion" aria-label="Region">
                                    <option value="australia-southeast1">Australia</option>
                                    <option value="northamerica-northeast2">Canada</option>
                                    <option value="US">United States</option>
                                </select>
                                <div class="form-text text-muted" v-if="!canChangeRegion && local_client != null && local_client.id > 0">
                                    Data residency cannot be changed once data has been uploaded.
                                </div>
                            </div>
                        </div>
                        <div class="mb-3" v-if="user.agency_id === 1">
                            <label class="form-label">NextAfter.io Client Name</label>
                            <select class="form-select" id="io-client-name" v-model="local_client.nextafterio_id" aria-label="NextAfter.io Client Name">
                                <option 
                                    v-for="i in nextafterio_clients" 
                                    :key="i.id" 
                                    :value="i.id"
                                >
                                    {{ i.name }}
                                </option>
                            </select>
                        </div>
                        
                        <!-- Admin-only fields -->
                        <div class="row" v-if="user.user_type === 'admin'">
                            <div class="col mb-3" >
                                <label class="form-label">Plan Level</label>
                                <select class="form-select" v-model="local_client.plan_level" aria-label="Plan Level">
                                    <option value="">Select Plan Level</option>
                                    <option value="trial">Trial</option>
                                    <option value="benchmarks">Benchmarks</option>
                                    <option value="insights">Insights</option>
                                </select>
                            </div>

                            <div class="col-md-6 col-12 mb-3" v-if="local_client.plan_level == 'trial'">
                                <label class="form-label">Trial End Date</label>
                                <DatepickerUtc 
                                    v-model="local_client.trial_end_date"
                                    :disabled="false"
                                    input-format="MM/dd/yyyy"
                                    input-class="form-control"
                                    :class="{ 'is-invalid': errors.trial_end_date }"
                                />
                                <div class="text-danger invalid-feedback" v-show="errors.trial_end_date">
                                    {{ errors.trial_end_date }}
                                </div>
                            </div>                   
                        </div>

                        <div class="mb-3">
                            <label class="form-label">Slack Webhook URL
                                <a href='https://docs.slack.dev' target="_blank"> 
                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="Slack Webhook URLs have to be set up for each client so the messages go to a client-specific channel. Click on this icon to add a new webhook or find an existing one." alt=""></img>
                                </a>
                            </label>
                            <input 
                                type="text" 
                                class="form-control" 
                                :class="{ 'is-invalid': errors.slack_url }"
                                name="slack_url" 
                                v-model="local_client.slack_url" 
                                @update:modelValue="validateURL"
                             aria-label="Text input">
                            <div class="text-danger invalid-feedback" v-show="errors.slack_url">
                                {{ errors.slack_url }}
                            </div>
                        </div>
                        <div class="mb-3" v-if="user.agency_id === 1">
                            <label class="form-label">Billable Media Types</label>
                            <div class="dropdown">
                                <button 
                                    class="btn form-select btn-field" 
                                    type="button" 
                                    id="dropdownMenuButton" 
                                    data-bs-toggle="dropdown" 
                                    aria-haspopup="true" 
                                    aria-expanded="false" 
                                    data-bs-auto-close="outside"
                                >
                                    {{ local_client.billable_media_types?.length > 0 ? local_client.billable_media_types.join(", ") : 'Please Select' }}
                                </button>
                                <div class="dropdown-menu dropdown-menu-primary" aria-labelledby="dropdownMenuButton">
                                    <label 
                                        class="dropdown-item" 
                                        v-for="item in media_types" 
                                        :key="item"
                                    >
                                        <input 
                                            class="form-check-input" 
                                            type="checkbox" 
                                            :value="item" 
                                            v-model="local_client.billable_media_types"
                                         aria-label="Checkbox">
                                        <span class="ms-2">{{ item }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
                <!-- Image Modal -->
                <image-edit-modal 
                    :id="'image-edit-modal-client'" 
                    :current-image="local_client.image" 
                    @image-updated="handleImageUpdate" 
                    ref="imageEditModalClient" />
            </div>
        </div>
        
        <!-- Avid Admin Settings Card -->
        <div class="card mt-4" v-if="user.user_type === 'admin'">
            <div class="card-body">
                <h3 class="mb-3">Avid Admin Settings</h3>
                <div class="row">
                    <div class="col-md-4 col-12">
                        <div class="mb-3">
                            <label class="form-label">Payment Method</label>
                            <select 
                                class="form-select" 
                                v-model="local_client.payment_method"
                                :class="{ 'is-invalid': errors.payment_method }"
                                @change="errors.payment_method = ''"
                             aria-label="Payment Method">
                                <option value="stripe">Stripe</option>
                                <option value="ach">ACH</option>
                                <option value="bill.com">Bill.com</option>
                                <option value="other">Other</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-show="errors.payment_method">
                                {{ errors.payment_method }}
                            </div>
                        </div>
                    </div>
                    <div class="col-md-4 col-12">
                        <div class="mb-3">
                            <label class="form-label">Payment Terms</label>
                            <select 
                                class="form-select" 
                                v-model="local_client.payment_terms"
                                :class="{ 'is-invalid': errors.payment_terms }"
                                @change="errors.payment_terms = ''"
                             aria-label="Payment Terms">
                                <option value="immediate">Immediate</option>
                                <option value="net_15">Net 15</option>
                                <option value="net_30">Net 30</option>
                                <option value="net_60">Net 60</option>
                                <option value="net_90">Net 90</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-show="errors.payment_terms">
                                {{ errors.payment_terms }}
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-md-4 col-12">
                        <div class="mb-3">
                            <label class="form-label">&nbsp;</label>
                            <div class="form-check ms-3 mt-1" >
                                <input 
                                    class="form-check-input" 
                                    type="checkbox" 
                                    v-model="local_client.tax_exempt"
                                    id="tax_exempt"
                                 aria-label="&nbsp;">
                                <label class="form-check-label" for="tax_exempt">
                                    Tax Exempt
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Fiscal Year & Donor Settings -->
        <fiscal-year-settings 
            ref="fiscalYearSettings"
            :client="local_client"
            :saveEnabled="false">
        </fiscal-year-settings>

        <div class="row">
            <div class="col">
                <!-- Success Message -->
                <div class="alert alert-success mt-3" v-show="form.successful">
                    Your organization's information has been updated!
                </div>
                <div class="alert alert-danger mt-3" v-show="form.error">
                    There is an error in the fields above. Please correct and try again.
                </div>
                <!-- Update Button -->
                <div class="mt-3">
                    <button type="submit" class="btn btn-primary btn-lg" @click.prevent="update" :disabled="form.busy || hasError()">
                        <img class="icon me-2" src="/img/icons/avid-icons/save.svg" alt=""> 
                        {{ (client != null && client.id > 0) ? "Update" : "Add Client" }}
                    </button>
                    <button v-if="client != null && client.id > 0" type="button" class="btn btn-secondary btn-lg ms-2" :disabled="form.busy" @click.prevent="back" >
                        <img class="icon me-2" src="/img/icons/avid-icons/close.svg" alt=""> Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vSelect from "vue3-select";
    import ImageDropzone from '../components/ImageDropzone.vue';
    import DatepickerUtc from '../components/DatepickerUtc.vue';
    import debounce from 'lodash.debounce';
    import Swal from 'sweetalert2';
    import ImageEditModal from '../components/ImageEditModal.vue';
    import FiscalYearSettings from '../settings/organization_profile/FiscalYearSettings.vue';

    export default {
        name: 'ManageClients',
        components: {
            ImageDropzone,
            ImageEditModal,
            FiscalYearSettings,
            vSelect,
            DatepickerUtc
        },
        props: {
            client: {
                type: Object,
                default: null
            },
            user: {
                type: Object
            },
            agencies: {
                type: Array
            },
        },
        data() {
            return {
                form: {
                    successful: false,
                    busy: false,
                    error: false
                },
                errors: {
                    name: "",
                    slack_url: "",
                    domain: "",
                    vertical: "",
                    image: "",
                    trial_end_date: "",
                    payment_method: "",
                    payment_terms: ""
                },
                agency_id: null,
                nextafterio_clients: [{
                    id: -1,
                    name: "",
                    na_dataset: ""
                }],
                local_client: {
                    referring_agency_id: null
                },
                media_types: ['Email Prospecting', 'Facebook', "Google Grant", 'Other', 'Pay Per Click'],
                vertical_types: ['Arts, Culture, and Humanities', "Animal-Related", "Education", "Environment", "General Nonprofit", "Health & Human Services", 'Public Policy/Advocacy', 'Religion'],
                prioritization_options: [],
                user_prioritization: [],
                debouncedSearchOptions: null,
                local_org_list: [],
                searching: false,
                canChangeRegion: true
            };
        },
        created() {
            this.debouncedSearchOptions = debounce(this.searchOptions, 1000);
        },
        watch: {
            'local_client.referring_agency_id': {
                handler(newValue) {
                    // Ensure the value is properly converted to integer for the select
                    if (this.local_client && newValue !== null && newValue !== '' && newValue !== undefined) {
                        const parsed = parseInt(newValue);
                        if (!isNaN(parsed) && this.local_client.referring_agency_id !== parsed) {
                            this.local_client.referring_agency_id = parsed;
                        }
                    }
                },
                immediate: true
            },
            'local_client.plan_level': {
                handler(newValue) {
                    // Clear trial_end_date error when plan level is not trial
                    if (this.local_client && newValue !== 'trial') {
                        this.errors.trial_end_date = '';
                    }
                }
            },
            'local_client.trial_end_date': {
                handler(newValue) {
                    // Clear trial_end_date error when a valid date is selected
                    if (this.local_client && newValue && newValue !== '' && this.local_client.plan_level === 'trial') {
                        this.errors.trial_end_date = '';
                    }
                }
            }
        },
        beforeMount() {
            if (this.client != null) {
                if (this.client.id == -1) {
                    this.local_client = { ...this.getNewClient(), ...this.local_client };
                    this.agency_id = this.client.agency_id;
                } else {
                    this.local_client = { ...this.client, ...this.local_client };
                    if (this.local_client.agencies) {
                        if (this.local_client.agencies.length == 1)
                            this.agency_id = this.local_client.agencies[0].id;
                        else if (this.local_client.agencies.length > 1)
                            this.agency_id = -1;
                    }
                }

                if (this.local_client.giving_levels == null)
                    this.local_client.giving_levels = this.getDefaultGivingLevels();

                if (this.local_client.vertical == null)
                    this.local_client.vertical = [];
                
                // Ensure plan_level has a default value
                if (!this.local_client.plan_level || this.local_client.plan_level === '') {
                    this.local_client.plan_level = 'trial';
                }

                // Ensure referring_agency_id is properly initialized
                if (this.local_client.referring_agency_id != null) {
                    this.local_client.referring_agency_id = parseInt(this.local_client.referring_agency_id);
                }
                
                // Ensure tax_exempt is properly converted to boolean
                if (this.local_client.tax_exempt !== undefined) {
                    this.local_client.tax_exempt = this.local_client.tax_exempt === true || this.local_client.tax_exempt === 1 || this.local_client.tax_exempt === '1';
                }
            } else {
                this.local_client = { ...this.getNewClient(), ...this.local_client };
            }
        },
        mounted() {
            this.refreshData();
            this.checkCanChangeRegion();

            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            tooltipTriggerList.forEach((trigger, index) => {
                trigger.addEventListener('click', () => {
                    tooltipList[index].hide();
                });
                trigger.addEventListener('mouseleave', () => {
                    tooltipList[index].hide();
                });
            });
        },
        methods: {
            async checkOrganization() {
                this.errors.name = '';

                if (this.local_client.name != null && this.local_client.name != '') {
                    try {
                        await window.axios.post('/api/register/organization_exists', {
                            name: this.local_client.name,
                            ein: this.local_client.ein
                        });
                    } catch (error) {
                        if (error.response?.status === 422) {
                            this.errors.name = error.response.data.message;
                        }
                    }
                }
            },
            async searchOptions(search_name) {
                this.searching = true;
                try {
                    const response = await window.axios.post('/api/client/api_lookup', { name: search_name });
                    this.local_org_list = [];
                    if (response.data.results != null) {
                        this.local_org_list = response.data.results.map(result => ({
                            ein: result.ein,
                            revenue: result.revenues_total.value,
                            ntee: result.ntee.value,
                            label: result.name
                        }));
                    }
                } catch (error) {
                    console.error('Error searching options:', error);
                } finally {
                    this.searching = false;
                }
            },
            async selectOrg(org_selected) {
                if (org_selected) {
                    this.local_client.name = org_selected.label;
                    this.local_client.ein = org_selected.ein;

                    await this.checkOrganization();
                    
                    if (org_selected.revenue != null) {
                        if (org_selected.revenue < 1000000)
                            this.local_client.size = "Less than $1M";
                        else if (org_selected.revenue < 5000000)
                            this.local_client.size = "$1M-$5M";
                        else if (org_selected.revenue < 10000000)
                            this.local_client.size = "$5M-$10M";
                        else if (org_selected.revenue < 25000000)
                            this.local_client.size = "$10M-$25M";
                        else if (org_selected.revenue < 50000000)
                            this.local_client.size = "$25M-$50M";
                        else
                            this.local_client.size = "$50M+";
                    }

                    if (org_selected.ntee) {
                        if (org_selected.ntee[0] === 'A')
                            this.local_client.vertical = 'Arts, Culture, and Humanities';
                        else if (org_selected.ntee[0] === 'B')
                            this.local_client.vertical = 'Education';
                        else if (org_selected.ntee[0] === 'C')
                            this.local_client.vertical = 'Environment';
                        else if (org_selected.ntee[0] === 'D')
                            this.local_client.vertical = 'Animal-Related';
                        else if (['E', 'F', 'G', 'H', 'M', 'P', 'K'].includes(org_selected.ntee[0]))
                            this.local_client.vertical = "Health & Human Services";
                        else if (['Q', 'R', 'V'].includes(org_selected.ntee[0]))
                            this.local_client.vertical = "Public Policy/Advocacy";
                        else if (org_selected.ntee[0] === 'X')
                            this.local_client.vertical = 'Religion';
                        else
                            this.local_client.vertical = 'General Nonprofit';
                        
                        this.errors.vertical = '';
                    }

                    try {
                        const response = await window.axios.post('/api/client/api_lookup', { ein: this.local_client.ein });
                        if (response.data) {
                            if (response.data.website_url != null && response.data.website_url !== "") {
                                const url = new URL(response.data.website_url);
                                let host = url.hostname;
                                if (host.startsWith("www."))
                                    host = host.substring(4);
                                this.local_client.domain = host;
                            } else {
                                this.local_client.domain = "";
                            }
                            this.local_client.mission = response.data.description;
                            this.local_client.fiscal_year_offset = this.getFiscalYearStart(response.data.fiscal_year_end);
                            this.$forceUpdate();
                        }
                    } catch (error) {
                        console.error('Error fetching organization details:', error);
                    }
                }
            },
            getFiscalYearStart(fiscalYearEnd) {
                const months = {
                    "January": 1,
                    "February": 2,
                    "March": 3,
                    "April": 4,
                    "May": 5,
                    "June": 6,
                    "July": 7,
                    "August": 8,
                    "September": 9,
                    "October": 10,
                    "November": 11,
                    "December": 0
                };

                // Find the numerical value for the fiscal year start
                return months[fiscalYearEnd];
            },
            addNewOrg(search) {
                this.local_client.ein = "";
                this.local_client.name = search;
                this.local_client.mission = "";
                this.local_client.size = "";
                this.checkOrganization();

                if (this.$refs.orgSelect) {
                    this.$refs.orgSelect.open = false;
                    this.$refs.orgSelect.$refs.search.blur();
                }

                this.errors.name = '';
            },
            async refreshData() {
                try {
                    const response = await window.axios.get('/api/outbound/nextafterio');
                    this.nextafterio_clients = response.data.clients;
                } catch (error) {
                    console.error('Error refreshing data:', error);
                }
            },
            getNewClient() {
                return {
                    id: -1,
                    url: null,
                    name: "",
                    photo_url: "",
                    image: "",
                    nextafterio_id: null,
                    na_dataset: null,
                    billable_media_types: [],
                    vertical: "",
                    ein: null,
                    domain: "",
                    size: null,
                    slack_url: null,
                    fiscal_year_offset: 0,
                    giving_levels: this.getDefaultGivingLevels(),
                    default_reporting_period: 'Fiscal Year to Date',
                    users: [],
                    why_give: '',
                    style_guide: '',
                    client_mission: '',
                    plan_level: 'trial',
                    referring_agency_id: null,
                    //90 days from now
                    trial_end_date: new Date(new Date().setDate(new Date().getDate() + 90)).toISOString().split('T')[0],
                    region: 'US',
                    // Billing fields
                    tax_exempt: true,
                    payment_method: 'stripe',
                    payment_terms: 'immediate'
                };
            },
            getDefaultGivingLevels() {
                return {
                    split: 'HPC',
                    levels: [
                        { name: "Major", min: 10000, report: true },
                        { name: "Midlevel", min: 1000, report: true },
                        { name: "Broadbase", min: 0, report: false }
                    ]
                };
            },

            async validateName() {
                try {
                    const response = await window.axios.post('/api/client/check_name', this.local_client);
                    if (response.status === 200) {
                        this.errors.name = response.data.exists ? 'The client name entered already exists.' : '';
                    }
                } catch (error) {
                    console.error('Error validating name:', error);
                }
            },
            validateURL() {
                if (this.local_client.slack_url != null && this.local_client.slack_url !== "") {
                    if (!this.local_client.slack_url.startsWith("https://hooks.slack.com/services/")) {
                        this.errors.slack_url = "A valid Slack Webhook URL should start with https://hooks.slack.com/services/";
                        return;
                    }
                }
                this.errors.slack_url = '';
            },
            isValid() {
                this.form.error = false;

                if (this.local_client.name === "") {
                    this.errors.name = "The organization name is required.";
                    this.form.error = true;
                } else {
                    this.errors.name = "";
                }

                if (!this.local_client.domain || this.local_client.domain.trim() == '') {
                    this.errors.domain = "The website domain is required.";
                    this.form.error = true;
                } else {
                    // Remove any protocol and trailing slashes
                    const domain = this.local_client.domain.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '');
                    // Ensure it's a valid domain name with optional www prefix
                    const regex = /^(?:www\.)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
                    if (!regex.test(domain)) {
                        this.errors.domain = "The website domain is invalid.";
                        this.form.error = true;
                    } else {
                        this.errors.domain = "";
                    }
                }

                if (this.local_client.vertical === "") {
                    this.errors.vertical = "The vertical is required.";
                    this.form.error = true;
                } else {
                    this.errors.vertical = "";
                }

                // Validate trial_end_date is required when plan level is trial
                if (this.local_client.plan_level === "trial" && (!this.local_client.trial_end_date || this.local_client.trial_end_date === "")) {
                    this.errors.trial_end_date = "The trial end date is required when plan level is trial.";
                    this.form.error = true;
                } else {
                    this.errors.trial_end_date = "";
                }

                // Check fiscal year settings component for validation errors
                if (this.$refs.fiscalYearSettings && this.$refs.fiscalYearSettings.hasValidationErrors()) {
                    this.form.error = true;
                }

                return !this.form.error;
            },
            async update() {
                if (!this.isValid())
                    return;

                this.form.busy = true;

                if (this.local_client.nextafterio_id != null && this.local_client.nextafterio_id > 0) {
                    const nextafterioClient = this.nextafterio_clients.find(c => c.id === this.local_client.nextafterio_id);
                    if (nextafterioClient) {
                        this.local_client.na_dataset = nextafterioClient.bigquery_dataset;
                        this.local_client.acq_dashboard_id = nextafterioClient.acq_dashboard_id;
                    }
                }

                if (this.agency_id != null && this.agency_id > 0)
                    this.local_client.agency_id = this.agency_id;

                if (this.user_prioritization.length > 0)
                    this.local_client.prioritization_preference = this.user_prioritization;

                try {  //Make a new client
                    if (this.local_client.id === -1) {
                        const response = await window.axios.post('/api/client', this.local_client);
                        
                        await Swal.fire({
                            title: "Client created!",
                            text: this.local_client.name + " has been created.",
                            icon: "success",
                            showCancelButton: false,
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-success'
                            },
                        });
                    } else {
                        const response = await window.axios.put(`/api/client/${this.local_client.url}`, this.local_client);
                        
                        await Swal.fire({
                            title: "Client updated!",
                            text: this.local_client.name + " has been updated.",
                            icon: "success",
                            showCancelButton: false,
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-success'
                            },
                        });
                    }

                    if (this.agency_id != null && this.agency_id > 0)
                        window.location.href = '/admin/agencies/' + this.agency_id + '/clients';
                    //Just go back to the previous page
                        window.history.back();
                } catch (error) {
                    console.error('Error updating client:', error);
                    this.form.error = true;
                } finally {
                    this.form.busy = false;
                }
            },
            hasError() {
                return this.errors.name !== "" || 
                       this.errors.slack_url !== "" || 
                       this.errors.domain !== "" || 
                       this.errors.vertical !== "" ||
                       this.errors.trial_end_date !== "" ||
                       this.errors.payment_method !== "" ||
                       this.errors.payment_terms !== "";
            },
            async s3UploadSuccess(s3ObjectLocation) {
                s3ObjectLocation = s3ObjectLocation.replace("%2F", "/");
                s3ObjectLocation = s3ObjectLocation.startsWith("http") ? s3ObjectLocation : 'https:'+ s3ObjectLocation;

                this.local_client.photo_url = s3ObjectLocation;
                this.local_client.image = s3ObjectLocation;

                try {
                    const response = await window.axios.post('/api/client/get_colors', this.local_client);
                    if (response.data.light_color != null) {
                        this.local_client.light_color = response.data.light_color;
                        this.local_client.dark_color = response.data.dark_color;
                    }
                } catch (error) {
                    console.error('Error getting colors:', error);
                }
            },
            handleImageUpdate(value) {
                if (value === '') {
                    // Image was deleted
                    this.local_client.photo_url = null;
                    this.local_client.image = null;
                    if (this.local_client.id !== -1) {
                        this.update();
                    }
                } else {
                    // New image was uploaded
                    this.s3UploadSuccess(value);
                }
            },
            openImageModal() {
                const el = document.getElementById('image-edit-modal-client');
                if (el) {
                    const modal = new window.bootstrap.Modal(el);
                    modal.show();
                }
            },
            back() {
                if (this.agency_id != null && this.agency_id > 0)
                    window.location.href = '/admin/agencies/' + this.agency_id + '/clients';
                //Just go back to the previous page
                window.history.back();
            },
            async checkCanChangeRegion() {
                // Only check if client exists and has an ID
                if (this.local_client && this.local_client.id > 0 && this.local_client.url) {
                    try {
                        const response = await window.axios.get(`/api/client/${this.local_client.url}/can_change_region`);
                        this.canChangeRegion = response.data.can_change_region;
                    } catch (error) {
                        console.error('Error checking if region can be changed:', error);
                        // Default to false if there's an error to be safe
                        this.canChangeRegion = false;
                    }
                } else {
                    // New clients can always set their region
                    this.canChangeRegion = true;
                }
            }
        }
    }
</script>
